import { getDocumentElement } from "../../Utils/Utils";

/**
 * Central function for all Dam volume calculations.
 * 
 * @param           {object}    options
 * 
 * @returns         {void}
*/
export const AllDamVolumeCalc = (options) => {
    // Identifiers
    const DamType = options.type;
    const ID = options.accordionNumber;
    const BatterSlope = 3;

    // Fields
    const AnnualEvaporationField = getDocumentElement(`${options.selectors.annualEvaporationField}`);
    const AnnualRainfallField = getDocumentElement(`${options.selectors.annualRainfallField}`);
    const DepthField = getDocumentElement(`${options.selectors.depthField}-${ID}`);
    const RadiusField = getDocumentElement(`${options.selectors.radiusField}-${ID}`);
    const SurfaceAreaField = getDocumentElement(`${options.selectors.surfaceAreaField}-${ID}`);
    const WidthField = getDocumentElement(`${options.selectors.widthField}-${ID}`);
    const LengthField = getDocumentElement(`${options.selectors.lengthField}-${ID}`);
    const DamEvaporationField = getDocumentElement(`${options.selectors.damEvaporationField}-${ID}`);
    const RainSurfaceField = getDocumentElement(`${options.selectors.rainSurfaceField}-${ID}`);
    const DamVolumeField = getDocumentElement(`${options.selectors.damVolumeField}-${ID}`);
    const SeepageField = getDocumentElement(`${options.selectors.seepageField}-${ID}`);

    // This accordion and error field
    const Accordion = getDocumentElement(`.farm-calc-accordion[data-id="dam"][data-number="${ID}"]`);
    const ErrorField = Accordion.querySelector('.farm-calc-accordion__error-msg');
    
    /**
     * Run the circular dam type calculation.
     * 
     * @returns     {void}
     */
    const __circularSurfaceAreaCalc = () => {
        let calc = Math.PI * Math.pow(parseFloat(RadiusField.value), 2);
        SurfaceAreaField.value = calc.toFixed(1);
    };

    /**
     * Run the rectangle/square dam type calculation.
     * 
     * @returns     {void}
     */
    const __rectangleSquareSurfaceAreaCalc = () => {
        let calc = parseFloat(LengthField.value) * parseFloat(WidthField.value);
        SurfaceAreaField.value = calc.toFixed(1);
    };

    /**
     * Run the rectangle/square dam type calculation.
     * 
     * @returns     {void}
     */
    const __gullySurfaceAreaCalc = () => {
        let calc = 0.5 * parseFloat(LengthField.value) * parseFloat(WidthField.value);
        SurfaceAreaField.value = calc.toFixed(1);
    };

    /**
     * Run the evaporative dam calculation.
     * 
     * @returns     {void}
     */
    const __evaporativeCalc = () => {
        let calc = (parseFloat(AnnualEvaporationField.value) * parseFloat(SurfaceAreaField.value) * 0.67) / 1000000;
        DamEvaporationField.value = calc.toFixed(3);
    };

    /**
     * Run the rain on dam surface calculation.
     * 
     * @returns     {void}
    */
    const __rainOnDamSurfaceCalc = () => {
        let calc = (parseFloat(SurfaceAreaField.value) * parseFloat(AnnualRainfallField.value)) / 1000000;
        RainSurfaceField.value = calc.toFixed(3);
    }

    /**
     * Inserts an error message into the ErrorField element.
     * Uses a timeout to prevent MutationObservers from immediately overwriting the content.
     *
     * @param {string} message - The error message to display.
     */
    const __addErrorMessage = (message) => {
        setTimeout(() => {
            ErrorField.innerHTML = `<p class="error-text">${message}</p>`;
        }, 0);
    };

    /**
     * Clears any error message currently displayed in the ErrorField element.
     * Uses a timeout to prevent conflicts with MutationObservers.
     */
    const __clearErrorMessage = () => {
        setTimeout(() => {
            ErrorField.innerHTML = '';
        }, 0);
    };

    /**
     * Circular dam volume calculation.
     * 
     * @returns     {void}
     */
    function __circularDamVolumeCalc() {
        const Rt = parseFloat(RadiusField.value);
        const D = parseFloat(DepthField.value);

        const π = Math.PI;

        // Calculate bottom radius
        const Rb = Rt - 2 * D * BatterSlope;

        // Validate
        if (Rb < 0) {
            DamVolumeField.value = '';
            __addErrorMessage(`The base radius is a negative number (${Rb}), please readjust the depth or radius`)
            return;
        }

        // Clear any previous error msg
        __clearErrorMessage();

        // Surface area (top)
        const surfaceArea = π * Rt * Rt;  // m²

        // Volume (ML)
        const volumeML = ((π * Rt * Rt + π * Rb * Rb + π * (Rt + Rb) * (Rt + Rb)) / 6000) * D;

        // Evaporation loss (ML/year)
        const evaporationLossML = (AnnualEvaporationField.value * surfaceArea * 0.67) / 1_000_000;

        // Seepage loss (ML/year)
        const seepageLossML = volumeML * 0.1;

        // Rainfall onto dam (ML/year)
        const rainfallML = (surfaceArea * AnnualRainfallField.value) / 1_000_000;

        const result = {
            volumeML: volumeML.toFixed(2),
            evaporationLossML: evaporationLossML.toFixed(2),
            seepageLossML: seepageLossML.toFixed(2),
            rainfallML: rainfallML.toFixed(2),
            surfaceArea: surfaceArea.toFixed(2),
            bottomRadius: Rb.toFixed(2)
        };

        DamVolumeField.value = result.volumeML;
    }

    /**
     * Rectangle/square dam volume calculation.
     * 
     * @returns     {void}
     */
    const __rectangleSquareDamVolumeCalc = () => {
        // Setup calculation values
        const LengthT = parseFloat(LengthField.value);
        const WidthT = parseFloat(WidthField.value);
        const Depth = parseFloat(DepthField.value);
        const LengthB = LengthT - (2 * (Depth * BatterSlope));
        const WidthB = WidthT - (2 * (Depth * BatterSlope));
        let calc = 0;

        // Run main calculation
        const FirstPortion = [(LengthT * WidthT) + (LengthB * WidthB) + ((LengthT + LengthB) * (WidthT + WidthB))];
        calc = (parseFloat(FirstPortion) / 6000) * Depth;

        DamVolumeField.value = calc.toFixed(3);
    };

    /**
     * Gully dam volume calculation.
     * 
     * @returns     {void}
     */
    const __gullyDamVolumeCalc = () => {
        let calc = (parseFloat(DepthField.value) * parseFloat(SurfaceAreaField.value) * 0.4) / 1000;
        DamVolumeField.value = calc.toFixed(3);
    };

    /**
     * Run the seepage losses calculation.
     * 
     * @returns     {void}
     */
    const __seepageLossesCalc = () => {
        let calc = parseFloat(DamVolumeField.value) * 0.1;
        SeepageField.value = calc.toFixed(3);
    };

    /**
     * Init all calcs.
     * 
     * @returns     {void}
    */
    const init = () => {    
        if (DepthField &&
            RadiusField &&
            SurfaceAreaField &&
            WidthField &&
            LengthField &&
            DamEvaporationField &&
            AnnualEvaporationField &&
            RainSurfaceField &&
            DamVolumeField
        ) {
            switch (DamType) {
                case 'circular':
                    __circularSurfaceAreaCalc();
                    __circularDamVolumeCalc();
                    break;
                case 'rectangleSquare':
                    __rectangleSquareSurfaceAreaCalc();
                    __rectangleSquareDamVolumeCalc();
                    break;
                case 'gully':
                    __gullySurfaceAreaCalc()
                    __gullyDamVolumeCalc();
                    break;
                default:
                    break;
            }

            __evaporativeCalc();
            __rainOnDamSurfaceCalc();
            __seepageLossesCalc();

            SurfaceAreaField.addEventListener('input', e => {
                e.stopImmediatePropagation();

                __evaporativeCalc();
                __rainOnDamSurfaceCalc();
                __seepageLossesCalc();
            });
        }
    };

    /**
     * Expose the init function.
     */
    return Object.freeze({ init });
};
